import { cn } from '@/lib/cn';

export interface IFormTab {
  id: number;
  label: string;
  disabled?: boolean;
  /** Tooltip explaining why the tab is unavailable, e.g. on a new record. */
  disabledHint?: string;
}

interface FormTabsProps {
  tabs: IFormTab[];
  activeTab: number;
  onChangeTab: (id: number) => void;
  className?: string;
}

/**
 * Tab strip that sits on top of a card panel: the rail carries the muted
 * surface, the active tab lifts out of it in the card colour so the panel
 * below reads as one continuous sheet. Shared so every tabbed screen —
 * activity form, pricing forms, smart suggestion — reads as one app.
 */
const FormTabs = ({
  tabs,
  activeTab,
  onChangeTab,
  className,
}: FormTabsProps) => (
  <div
    role="tablist"
    className={cn(
      'bg-neutral-80 flex w-max items-center rounded-t-2xl',
      className
    )}
  >
    {tabs.map(tab => {
      const isActive = tab.id === activeTab;

      return (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={isActive}
          disabled={tab.disabled}
          title={tab.disabled ? tab.disabledHint : undefined}
          onClick={() => onChangeTab(tab.id)}
          className={cn(
            'text-text-placeholder cursor-pointer rounded-t-2xl px-4 py-2 text-sm transition-all duration-200',
            isActive &&
              'text-base-orange-20 border-primary-100 bg-neutral-100 font-medium',
            tab.disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          {tab.label}
        </button>
      );
    })}
  </div>
);

export { FormTabs };
